import { lazy, Suspense } from 'react';
import { Navigate, useRoutes, type RouteObject } from 'react-router-dom';

import { Routes } from 'config';
import { useLoggerHook } from 'hooks';
import { Loader } from 'components/loader';

// Eager load layouts (needed immediately)
import RootLayout from './layout';
import PrivateLayout from './(auth)/layout';
import FamilyInformationLayout from './(auth)/family-information/layout';

// Lazy load pages
const HomePage = lazy(() => import('./index'));
const NotFound = lazy(() => import('./(public)/not-found'));
const LoginPage = lazy(() => import('./(public)/login'));
const VerifyPage = lazy(() => import('./(public)/verify'));
const SignUpPage = lazy(() => import('./(public)/sign-up'));
const SelectVerifyPage = lazy(() => import('./(public)/select-verify'));
const ForgotPasswordPage = lazy(() => import('./(public)/forgot-password'));
const DashboardPage = lazy(() => import('./(auth)/dashboard'));
const ProfilePage = lazy(() => import('./(auth)/profile'));
const VaccinePage = lazy(() => import('./(auth)/vaccine'));
const ResourcesPage = lazy(() => import('./(auth)/resources'));
const ResourceDetailsPage = lazy(() => import('./(auth)/resources/details'));
const CompletedPage = lazy(() => import('./(auth)/completed'));
const ContactUsPage = lazy(() => import('./(auth)/contact-us'));
const CourseFormPage = lazy(() => import('./(auth)/forms/details'));
const AdditionalInfoPage = lazy(() => import('./(auth)/additional-info'));
const FamilyInformationPage = lazy(() => import('./(auth)/family-information'));
const FamilyInformationCreatePage = lazy(
  () => import('./(auth)/family-information/create')
);
const FamilyInformationDetailsPage = lazy(
  () => import('./(auth)/family-information/details')
);

const toChildPath = (path: string) => path.replace(/^\//, '');

const publicRoutes: RouteObject[] = [
  {
    element: <LoginPage />,
    path: toChildPath(Routes.Auth.Login),
  },
  {
    element: <SignUpPage />,
    path: toChildPath(Routes.Auth.Signup),
  },
  {
    element: <SelectVerifyPage />,
    path: toChildPath(Routes.Auth.SelectVerify),
  },
  {
    element: <VerifyPage />,
    path: toChildPath(Routes.Auth.Verify),
  },
  {
    element: <ForgotPasswordPage />,
    path: toChildPath(Routes.Auth.ForgotPassword),
  },
];

const familyInformationRoutes: RouteObject = {
  element: <FamilyInformationLayout />,
  path: toChildPath(Routes.FamilyInformation.Home),
  children: [
    {
      index: true,
      element: <FamilyInformationPage />,
    },
    {
      path: Routes.FamilyInformation.Create,
      element: <FamilyInformationCreatePage />,
    },
    {
      path: Routes.FamilyInformation.Edit,
      element: <FamilyInformationDetailsPage />,
    },
  ],
};

const privateRoutes: RouteObject[] = [
  {
    element: <DashboardPage />,
    path: toChildPath(Routes.Dashboard.Home),
  },
  {
    element: <ResourcesPage />,
    path: toChildPath(Routes.Resources.Home),
  },
  {
    element: <ResourceDetailsPage />,
    path: toChildPath(Routes.Course.Resource),
  },
  {
    element: <ContactUsPage />,
    path: toChildPath(Routes.ContactUs.Home),
  },
  {
    element: <ProfilePage />,
    path: toChildPath(Routes.Profile.Home),
  },
  {
    element: <CourseFormPage />,
    path: toChildPath(Routes.Course.Form),
  },
  {
    element: <VaccinePage />,
    path: toChildPath(Routes.Course.Vaccine),
  },
  {
    element: <CompletedPage />,
    path: toChildPath(Routes.Course.Completed),
  },
  {
    element: <AdditionalInfoPage />,
    path: toChildPath(Routes.AdditionalInfo.Home),
  },
  familyInformationRoutes,
];

export const appRoutes: RouteObject[] = [
  {
    path: Routes.Home,
    element: <RootLayout />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      ...publicRoutes,
      {
        children: privateRoutes,
        element: <PrivateLayout />,
      },
      {
        path: Routes.NotFound,
        element: <NotFound homePath={Routes.Home} />,
      },
      {
        path: '*',
        element: <Navigate replace to={Routes.NotFound} />,
      },
    ],
  },
];

export const AppRoutes = () => {
  const element = useRoutes(appRoutes);

  useLoggerHook();

  return <Suspense fallback={<Loader isLoading />}>{element}</Suspense>;
};
